import { useState } from "react";
import { useNavigate } from "react-router";

import invariant from "tiny-invariant";

import { TaskCard } from "entities/task";
import { GetBlumCertificateSheet } from "features/get-blum-certificate";
import { GetCertificateSheet, type Step } from "features/get-certificate";
import { Page } from "pages/ui/layout/page";
import { allTasks, Task } from "shared/config/consts/tasks";
import { ZkCertType } from "shared/config/consts/zk-cert-types";
import { useConfetti } from "shared/hooks/use-confetti";
import { useInitHolder } from "shared/providers/holder/use-init-holder";
import { useHolderStore } from "shared/stores/holder-store";
import { useCertificatesQuery } from "shared/tg/hooks/use-certificates-query";
import { useCompleteTaskMutation } from "shared/tg/hooks/use-complete-task-mutation";
import { useCompletedTasksQuery } from "shared/tg/hooks/use-completed-tasks-query";
import { CertificateMetadata } from "shared/tg/types";
import { haptic } from "shared/utils/tg";

import bgPng from "./assets/bg.png";
import { InviteSheet } from "./sheet";

export const Home = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isGetCertificateOpen, setIsGetCertificateOpen] = useState(false);
  const [isBlumOpen, setIsBlumOpen] = useState(false);

  const [initialType, setInitialType] = useState<ZkCertType | undefined>();
  const [initialStep, setInitialStep] = useState<Step>("select-type");

  const [holder] = useHolderStore();

  const jsConfetti = useConfetti();

  const initHolder = useInitHolder();

  const navigate = useNavigate();

  const certsQuery = useCertificatesQuery({
    holderCommitment: holder?.holderCommitment,
  });
  const completedTasksQuery = useCompletedTasksQuery();
  const completeTaskMutation = useCompleteTaskMutation();
  const completedTasks = completedTasksQuery.data || [];
  const repPoints = completedTasks.reduce((acc, item) => {
    acc = acc + item.reward;
    return acc;
  }, 0);

  const uncompletedTasks = allTasks.filter((t) => {
    return !completedTasks.some((task) => task.systemName === t.systemName);
  });

  const completeGeneratePrivateKeyTask = async () => {
    const holder = await initHolder();
    invariant(holder, "Generation keys failed");
    await completeTaskMutation.mutateAsync({
      systemName: "generate-private-key",
    });
    await jsConfetti.addConfetti();
  };

  // const completeKYCTask = async () => {
  //   invariant(
  //     certsQuery.data?.some((cert) => cert.standard === "gip1"),
  //     "Cannot find KYC certificate"
  //   );
  //   if (certsQuery.data?.some((cert) => cert.standard === "gip1")) {
  //     await completeTaskMutation.mutateAsync({
  //       systemName: "generate-gip1-cert",
  //     });
  //     await jsConfetti.addConfetti();
  //   }
  // };

  const completeBlumTask = async (certs: CertificateMetadata[]) => {
    if (certs.some((cert) => cert.standard === "gip2")) {
      await completeTaskMutation.mutateAsync({
        systemName: "generate-gip2-cert",
      });
      setIsBlumOpen(false);
      await jsConfetti.addConfetti();
    } else {
      await initHolder();
      setIsBlumOpen(true);
    }
  };

  const completeXTask = async (certs: CertificateMetadata[]) => {
    if (certs.some((cert) => cert.standard === "gip3")) {
      await completeTaskMutation.mutateAsync({
        systemName: "generate-gip3-cert",
      });
      await jsConfetti.addConfetti();
    } else {
      await initHolder();
      setInitialType("gip3");
      setInitialStep("select-provider");
      setIsGetCertificateOpen(true);
    }
  };

  const handleTaskClick = async (task: Task) => {
    haptic("light");
    if (task.systemName === "generate-private-key")
      await completeGeneratePrivateKeyTask();

    if (task.systemName === "generate-gip2-cert")
      await completeBlumTask(certsQuery.data ?? []);

    if (task.systemName === "generate-gip3-cert")
      await completeXTask(certsQuery.data ?? []);
  };

  const handleBlumSuccess = async (certs: CertificateMetadata[]) => {
    await completeBlumTask(certs);
  };

  return (
    <Page className="pt-0">
      <div
        style={{
          backgroundImage: `url(${bgPng})`,
        }}
        className="flex h-60 flex-col items-center justify-end bg-cover bg-center bg-no-repeat pb-10 text-white"
      >
        <div className="text-lg">My Reputation Points</div>
        <div
          className="mt-1 text-5xl font-semibold"
          onClick={() => {
            navigate("/dev");
          }}
        >
          {repPoints}
        </div>
      </div>

      <div className="mt-7.5 mb-3 overflow-y-auto px-6">
        <h1 className="text-lg">Main tasks</h1>
        {completedTasksQuery.isSuccess && (
          <ul className="mt-2.5 flex flex-col gap-3">
            {uncompletedTasks.map((t) => {
              const isActive = t.dependsOn.every((systemName) =>
                completedTasks.some((task) => task.systemName === systemName)
              );

              return (
                <TaskCard
                  key={t.systemName}
                  reward={t.reward}
                  status={isActive ? "active" : "blocked"}
                  title={t.title}
                  description={`+${t.reward} REP`}
                  onClick={() => handleTaskClick(t)}
                />
              );
            })}

            {completedTasks.map((t) => {
              return (
                <TaskCard
                  status="completed"
                  reward={t.reward}
                  key={t.systemName}
                  title={t.title}
                  description={`+${t.reward} REP`}
                  onClick={() => {}}
                />
              );
            })}
          </ul>
        )}
      </div>
      {/* <div className="mt-auto flex gap-x-3 px-6">
        <Button className="grow" outline onClick={() => setIsOpen(true)}>
          Invite Friends
        </Button>
        <Button
          outline
          onClick={() => {
            copy("Copy text");
          }}
        >
          {isCopied && <LuCopyCheck className="size-5" />}
          {!isCopied && <LuCopy className="size-5" />}
        </Button>
      </div> */}

      <InviteSheet onClose={() => setIsOpen(false)} isOpen={isOpen} />
      {isGetCertificateOpen && holder && (
        <GetCertificateSheet
          hideProgressBar
          isOpen={isGetCertificateOpen}
          holderCommitment={holder}
          initialStep={initialStep}
          initialType={initialType}
          onClose={() => {
            setIsGetCertificateOpen(false);
          }}
        />
      )}
      {holder && (
        <GetBlumCertificateSheet
          isOpen={isBlumOpen}
          holder={holder}
          onSuccess={handleBlumSuccess}
          onClose={() => setIsBlumOpen(false)}
        />
      )}
    </Page>
  );
};
